import { useState } from 'react';
import { ImportDialog, type ImportField } from '@/components/import/ImportDialog';
import { buildTemplate, TEMPLATE_CAISSE_B2C } from '@/lib/import-templates';
import { useImportCaisseB2C, type ImportCaisseB2CRow } from '@/hooks/queries/useImportCaisse';

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
}

const FIELDS: ImportField[] = TEMPLATE_CAISSE_B2C.headers.map((h) => ({
  key: h,
  label: h,
}));

export function ImportCaisseB2CDialog({ open, onOpenChange }: Props): JSX.Element {
  const importMut = useImportCaisseB2C();
  const [progress, setProgress] = useState<{ current: number; total: number } | null>(null);

  return (
    <>
      {progress && progress.total > 0 && (
        <div className="fixed bottom-6 right-6 z-50 rounded-xl border border-rose-300 bg-white p-4 shadow-2xl">
          <div className="mb-1 text-xs font-bold uppercase tracking-widest text-rose-700">
            Import B2C en cours
          </div>
          <div className="text-sm font-semibold text-slate-800">
            {progress.current} / {progress.total} lignes traitées
          </div>
          <div className="mt-2 h-1.5 w-64 overflow-hidden rounded-full bg-slate-200">
            <div
              className="h-full bg-rose-500 transition-all"
              style={{ width: `${Math.round((progress.current / progress.total) * 100)}%` }}
            />
          </div>
        </div>
      )}
      <ImportDialog<ImportCaisseB2CRow>
        open={open}
        onOpenChange={(o) => {
          onOpenChange(o);
          if (!o) setProgress(null);
        }}
        title="Importer la Caisse Formation B2C (CSV)"
        fields={FIELDS}
        generateTemplate={() => buildTemplate(TEMPLATE_CAISSE_B2C)}
        templateFilename={TEMPLATE_CAISSE_B2C.filename}
        requiredHeaders={TEMPLATE_CAISSE_B2C.requiredHeaders}
        onImport={async (rows) => {
          setProgress({ current: 0, total: rows.length });
          try {
            const r = await importMut.mutateAsync({
              rows,
              onProgress: (p) => setProgress(p),
            });
            const summary: string[] = [];
            summary.push(`✓ ${r.entrees} entrée(s) · ${r.sorties} sortie(s)`);
            if (r.tiers_crees > 0) summary.push(`${r.tiers_crees} tier(s) créé(s)`);
            const msg = summary.join(' · ');
            return {
              success: r.importees,
              errors: r.errors.length === 0 ? [msg] : [msg, ...r.errors],
            };
          } finally {
            setProgress(null);
          }
        }}
      />
    </>
  );
}
